/**
 * @fileoverview Component for displaying a dropdown for staff status.
 * This component includes a dropdown with active and inactive options.
 */

import { Listbox } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { useDeactivateMutation, useReactivateMutation } from '../../services/staff'

/**
 * @interface StaffStatusDropdownProps
 * @description Props for the StaffStatusDropdown component.
 * @property {Novoic.API.Staff} staff - The staff member to display the status for.
 */
interface StaffStatusDropdownProps {
    staff: Novoic.API.Staff
}

/**
 * @interface StatusOption
 * @description Interface for the status options.
 * @property {string} title - The title of the status option.
 * @property {'active' | 'inactive'} value - The value of the status option.
 * @property {JSX.Element} icon - The icon to display for the status option.
 */
type StatusOption = {
    title: string
    value: 'active' | 'inactive'
    icon: JSX.Element
}

/**
 * @constant statusOptions
 * @description The options for the staff status dropdown.
 * @type {StatusOption[]}
 */
const statusOptions: StatusOption[] = [
    {
        title: 'Active',
        value: 'active',
        icon: <div className="w-2 h-2 mr-2 bg-emerald-500 rounded-full"></div>,
    },
    {
        title: 'Inactive',
        value: 'inactive',
        icon: <div className="w-2 h-2 mr-2 bg-red-500 rounded-full"></div>,
    },
]

/**
 * @function StaffStatusDropdown
 * @description Component for displaying a dropdown for staff status.
 * @param {StaffStatusDropdownProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the staff status dropdown.
 */
export function StaffStatusDropdown({ staff }: StaffStatusDropdownProps) {
    const isVerified = staff.is_verified
    const isVerificationExpired = staff.is_verification_expired
    const [selected, setSelected] = useState(staff.blocked_at ? statusOptions[1] : statusOptions[0])
    const [deactivate] = useDeactivateMutation()
    const [reactivate] = useReactivateMutation()

    function onChange(status: StatusOption) {
        setSelected(status)
        if (status.value === 'active') {
            reactivate(staff.id)
        } else {
            deactivate(staff.id)
        }
    }

    if (isVerificationExpired) {
        return (
            <div className="flex w-fit bg-red-100 text-red-800 text-xs font-medium rounded-[10px] py-0.5 px-2.5 ml-2.5">
                Expired
            </div>
        )
    }

    if (!isVerified) {
        return (
            <div className="flex w-fit bg-gray-100 text-gray-800 text-xs font-medium rounded-[10px] py-0.5 px-2.5 ml-2.5">
                Pending
            </div>
        )
    }

    return (
        <Listbox value={selected} onChange={onChange}>
            <div className="flex w-fit cursor-pointer items-center text-gray-900 text-sm font-medium rounded-sm py-0.5 px-2.5 hover:bg-indigo-50">
                <Listbox.Button className="flex items-center mr-1">
                    {selected.icon}
                    <span>{selected.title}</span>
                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                </Listbox.Button>
            </div>

            <Listbox.Options className="absolute w-40 mt-1 py-2 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {statusOptions
                    .filter((status) => status !== selected)
                    .map((status, statusIdx) => (
                        <Listbox.Option
                            key={statusIdx}
                            className={({ active }) =>
                                `${active ? 'bg-gray-100 text-gray-900' : 'text-gray-900'}
                                  cursor-pointer select-none relative py-2 px-4`
                            }
                            value={status}
                        >
                            {({ selected, active }) => (
                                <div className="flex items-center">
                                    <span className={`${selected ? 'font-medium' : 'font-normal'}`}>
                                        {status.icon} {selected} {active}
                                    </span>
                                    <span>{status.title}</span>
                                </div>
                            )}
                        </Listbox.Option>
                    ))}
            </Listbox.Options>
        </Listbox>
    )
}
